import {
  Component,
  inject,
  signal,
  WritableSignal,
  input,
  effect,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { Avatar } from 'primeng/avatar';
import { AuthStore } from '@core/store/auth/auth.store';
import { PopoverModule } from 'primeng/popover';
import {
  Router,
  RouterLink,
  RouterLinkActive,
  NavigationEnd,
} from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IDRISORSECARD, IDRISORSESIDENAV } from '@core/utils/const';
import { AuthApiService } from '@core/services/auth.service';
import { tap, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CantiereDTO } from '@core/models/api/swagger-auth/cantieri.interface';
import { DrawerModule } from 'primeng/drawer';
import { CapacitorService } from '@core/services/mobile/capacitor.service';
import { TitleCasePipe } from '@angular/common';
import packageJson from '../../../../../../package.json';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';

@Component({
  selector: 'app-toolbar-sidebar',
  imports: [
    ButtonModule,
    DrawerModule,
    ToolbarModule,
    Avatar,
    PopoverModule,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    TitleCasePipe,
  ],
  templateUrl: './toolbar-sidebar.component.html',
  styleUrl: './toolbar-sidebar.component.scss',
})
export class ToolbarSidebarComponent {
  visibleSidebar2 = false;
  authStore = inject(AuthStore);
  private readonly router = inject(Router);
  readonly translateService = inject(TranslateService);
  readonly capacitor = inject(CapacitorService);

  selectedCantiere: CantiereDTO | null = null;

  cantieri = signal<CantiereDTO[]>([]);

  public dashboard$: Subject<string> = new Subject();

  sideNavRoutesToShow: WritableSignal<
    {
      title: string;
      img: string;
      path: string;
      idCard?: string;
      idSidenav?: string;
    }[]
  > = signal([]);

  sideNavRoutes: {
    title: string;
    img: string;
    path: string;
    idCard?: string;
    idSidenav?: string;
  }[] = [
    {
      img: 'assets/icons/sidenav/Sidenav_home_v2.svg',
      path: '/home',
      title: this.translateService.instant('sidenav.home'),
    },
    {
      img: 'assets/icons/sidenav/Sidenav_dashboard_v2.svg',
      path: '/dashboard',
      title: this.translateService.instant('sidenav.dashboard'),
    },
    {
      img: 'building',
      path: '/distribuzione-anagrafica-soggetti',
      title: this.translateService.instant('sidenav.registry_subject'),
      idCard: IDRISORSECARD.distribuzione_censimento,
      idSidenav: IDRISORSESIDENAV.distribuzione_anagrafica_soggetti,
    },
    {
      img: 'users',
      path: '/clienti',
      title: this.translateService.instant('sidenav.clients'),
      idCard: IDRISORSECARD.clienti_ambiti_cantieri,
      idSidenav: IDRISORSESIDENAV.clienti,
    },
    {
      img: './assets/icons/sidenav/Sidenav_kit_v2.svg',
      path: '/kit',
      title: this.translateService.instant('sidenav.kit'),
      idCard: IDRISORSECARD.distribuzione_censimento,
      idSidenav: IDRISORSESIDENAV.kit,
    },
    {
      img: './assets/icons/sidenav/Sidenav_campagne_v2.svg',
      path: '/campagne-programmate',
      title: this.translateService.instant('sidenav.scheduled_campaigns'),
      idCard: IDRISORSECARD.distribuzione_censimento,
      idSidenav: IDRISORSESIDENAV.campagne,
    },
    {
      img: 'assets/icons/sidenav/Sidenav_attività_v2.svg',
      path: '/attivita',
      title: this.translateService.instant('sidenav.activity_management'),
      idCard: IDRISORSECARD.distribuzione_censimento,
      idSidenav: IDRISORSESIDENAV.attivita,
    },

    {
      img: 'assets/icons/sidenav/Sidenav_ambiti.svg',
      path: '/ambiti-tariffari',
      title: this.translateService.instant('sidenav.tariff_scopes'),
      idCard: IDRISORSECARD.clienti_ambiti_cantieri,
      idSidenav: IDRISORSESIDENAV.ambiti,
    },

    {
      img: 'building',
      path: '/anagrafica-soggetti',
      title: this.translateService.instant('sidenav.registry_subject'),
      idCard: IDRISORSECARD.soggetti_utenze_condomini,
      idSidenav: IDRISORSESIDENAV.anagrafica_soggetti,
    },
    {
      img: './assets/icons/sidenav/Sidenav_attrezzature_v2.svg',
      path: '/attrezzature',
      title: this.translateService.instant('sidenav.attrezzature'),
      idCard: IDRISORSECARD.attrezzature,
      idSidenav: IDRISORSESIDENAV.anagrafica,
    },
    {
      img: 'truck',
      path: '/flotta',
      title: this.translateService.instant('sidenav.flotta'),
      idCard: IDRISORSECARD.flotta,
      idSidenav: IDRISORSESIDENAV.flotta,
    },
    {
      img: 'building',
      path: '/dettaglio-conferimento-ccr',
      title: this.translateService.instant('sidenav.transfer_details_ccr'),
      idCard: IDRISORSECARD.ccr,
      idSidenav: IDRISORSESIDENAV.dettaglio_conferimenti_ccr,
    },
    {
      img: 'building',
      path: '/guida-raccolta',
      title: this.translateService.instant('sidenav.collection_guide'),
      idCard: IDRISORSECARD.sportello,
      idSidenav: IDRISORSESIDENAV.guida_raccolta,
    },
    {
      img: 'building',
      path: '/calendario-raccolta',
      title: this.translateService.instant('sidenav.harvest_calendar'),
      idCard: IDRISORSECARD.sportello,
      idSidenav: IDRISORSESIDENAV.calendario_raccolta,
    },
    {
      img: 'assets/icons/sidenav/Sidenav_conferimenti_v2.svg',
      path: '/conferimenti',
      title: this.translateService.instant('sidenav.transfer_details_ccr'),
      idCard: IDRISORSECARD.conferimenti,
      idSidenav: IDRISORSESIDENAV.dettaglio_conferimenti,
    },
    {
      img: './assets/icons/sidenav/Sidenav_ticket_v2.svg',
      path: '/gestione-richieste-crm',
      title: 'sidenav.harvest_calendar',
      idCard: IDRISORSECARD.crm,
      idSidenav: IDRISORSESIDENAV.gestione_ticket,
    },
    {
      img: 'shopping-bag',
      path: '/lista-servizi',
      title: 'sidenav.registry_subject',
      idCard: IDRISORSECARD.servizi,
      idSidenav: IDRISORSECARD.servizi,
    },
    // {
    // img: 'calendar-clock',
    // path: '/servizi-giornalieri',
    // title: 'sidenav.service',
    // idCard: IDRISORSECARD.servizi,
    // idSidenav: IDRISORSESIDENAV.servizi_list,
    // },
    {
      img: 'bell',
      path: '/news-crm',
      title: 'sidenav.registry_subject',
      idCard: IDRISORSECARD.crm,
      idSidenav: IDRISORSESIDENAV.gestione_ticket,
    },
    {
      img: './assets/icons/sidenav/Sidenav_zone_v2.svg',
      path: '/tipologia-zona',
      title: 'sidenav.zonazione',
      idCard: IDRISORSECARD.zonazione,
      idSidenav: IDRISORSESIDENAV.tipologia_zona,
    },
    /* bolt e sign out non cambiare posizione */
    {
      img: 'bolt',
      path: '/impostazioni',
      title: this.translateService.instant('sidenav.impostazioni'),
    },
    {
      img: 'sign-out',
      path: '',
      title: this.translateService.instant('sidenav.sign_out'),
    },
  ];

  readonly sessionIdRisorsa = sessionStorage.getItem('idRisorsa');

  private updateSidenavRoutes(): void {
    const currentIdCard = sessionStorage.getItem('idRisorsa');
    const currentRoutes = [...this.sideNavRoutesToShow()];
    const dashboardIndex = currentRoutes.findIndex(
      (route) => route.path === '/dashboard',
    );
    const hideDashboard =
      (this.router.url.includes('/flotta') &&
        !environment.showDashboardFlotte) ||
      (this.router.url.includes('/guida-supporto') && !currentIdCard);

    if (hideDashboard) {
      if (dashboardIndex !== -1) {
        currentRoutes.splice(dashboardIndex, 1);
        this.sideNavRoutesToShow.set(currentRoutes);
      }
    } else if (dashboardIndex === -1 && currentRoutes.length > 0) {
      currentRoutes.splice(1, 0, {
        img: 'th-large',
        path: '/dashboard',
        title: this.translateService.instant('sidenav.dashboard'),
      });
      this.sideNavRoutesToShow.set(currentRoutes);
    }
  }

  // Writable signals per cliente e cantiere
  readonly clienteName = signal<string | null>(
    sessionStorage.getItem('clienteName'),
  );
  readonly cantiereName = signal<string | null>(
    sessionStorage.getItem('cantiereName'),
  );
  readonly version: string = packageJson.version;
  constructor() {
    this._setIdCantiereCliente();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.updateSidenavRoutes();
      });

    // Effect per monitorare i cambiamenti negli ID selezionati nello store
    effect(() => {
      // Monitora i cambiamenti di idClienteSelected
      const idCliente = this.authStore.idClienteSelected();
      if (idCliente) {
        // Aggiorna il signal quando cambia l'ID cliente
        this.clienteName.set(sessionStorage.getItem('clienteName'));
      }
    });

    effect(() => {
      // Monitora i cambiamenti di idCantiereSelected
      const idCantiere = this.authStore.idCantiereSelected();
      if (idCantiere) {
        // Aggiorna il signal quando cambia l'ID cantiere
        this.cantiereName.set(sessionStorage.getItem('cantiereName'));
      }
    });

    this.authStore.idRisorsa$
      .pipe(
        tap(() => {
          // verifica se l'utente è admin
          const user = this.authStore.user();
          const isAdmin = AuthApiService.isAdmin(user);

          // per admin: richiede solo idCliente (idRisorsa opzionale)
          // per utenti normali: richiede idRisorsa, idCantiere e idCliente
          const hasRequiredData = isAdmin
            ? sessionStorage.getItem('idCliente')
            : sessionStorage.getItem('idRisorsa') &&
              sessionStorage.getItem('idCantiere') &&
              sessionStorage.getItem('idCliente');

          if (hasRequiredData) {
            const listIdRisorseSidenav = JSON.parse(
              sessionStorage.getItem('listIdRisorseSidenav') || '[]',
            );

            this.sideNavRoutesToShow.set(
              this.sideNavRoutes.filter((route) =>
                listIdRisorseSidenav.some(
                  (res: any) => res.idRisorsa === route.idSidenav,
                ),
              ),
            );

            if (sessionStorage.getItem('idRisorsa')) {
              const hideDashboard =
                this.router.url.includes('/flotta') &&
                !environment.showDashboardFlotte;
              const homeEntry = {
                img: './assets/icons/Home.svg',
                path: '/home',
                title: this.translateService.instant('sidenav.home'),
              };
              if (hideDashboard) {
                this.sideNavRoutesToShow().unshift(homeEntry);
              } else {
                this.sideNavRoutesToShow().unshift(homeEntry, {
                  img: 'th-large',
                  path: '/dashboard',
                  title: this.translateService.instant('sidenav.dashboard'),
                });
              }
            } else {
              this.sideNavRoutesToShow().unshift({
                img: 'assets/icons/sidenav/Sidenav_home_v2.svg',
                path: '/home',
                title: this.translateService.instant('sidenav.home'),
              });
            }
            let sidenavTopPush = this.capacitor.isNative()
              ? [
                  {
                    img: 'bolt',
                    path: '/impostazioni',
                    title: this.translateService.instant(
                      'sidenav.impostazioni',
                    ),
                  },
                  {
                    img: 'sign-out',
                    path: '',
                    title: this.translateService.instant('sidenav.sign_out'),
                  },
                ]
              : [
                  {
                    img: 'sign-out',
                    path: '',
                    title: this.translateService.instant('sidenav.sign_out'),
                  },
                ];

            this.sideNavRoutesToShow().push(...sidenavTopPush);
          } else {
            // per utenti normali senza cantiere solo logout
            // per admin senza dati completi Home + logout
            const user = this.authStore.user();
            const isAdmin = AuthApiService.isAdmin(user);
            let sidenavTopPush = this.capacitor.isNative()
              ? [
                  {
                    img: './assets/icons/Home.svg',
                    path: '/home',
                    title: this.translateService.instant('sidenav.home'),
                  },
                  {
                    img: 'bolt',
                    path: '/impostazioni',
                    title: this.translateService.instant(
                      'sidenav.impostazioni',
                    ),
                  },
                  {
                    img: 'sign-out',
                    path: '',
                    title: this.translateService.instant('sidenav.sign_out'),
                  },
                ]
              : [
                  {
                    img: './assets/icons/Home.svg',
                    path: '/home',
                    title: this.translateService.instant('sidenav.home'),
                  },

                  {
                    img: 'sign-out',
                    path: '',
                    title: this.translateService.instant('sidenav.sign_out'),
                  },
                ];
            if (isAdmin) {
              this.sideNavRoutesToShow.set([...sidenavTopPush]);
            } else {
              let sidenavTopPush = this.capacitor.isNative()
                ? [
                    {
                      img: 'bolt',
                      path: '/impostazioni',
                      title: this.translateService.instant(
                        'sidenav.impostazioni',
                      ),
                    },
                    {
                      img: 'sign-out',
                      path: '',
                      title: this.translateService.instant('sidenav.sign_out'),
                    },
                  ]
                : [
                    {
                      img: 'sign-out',
                      path: '',
                      title: this.translateService.instant('sidenav.sign_out'),
                    },
                  ];
              this.sideNavRoutesToShow.set([...sidenavTopPush]);
            }
          }
          this.translateService.instant('it');
        }),
      )
      .subscribe();
  }

  private _setIdCantiereCliente(): void {
    const user = this.authStore.user();
    const isAdmin = AuthApiService.isAdmin(user);

    if (sessionStorage.getItem('idCliente')) {
      this.authStore.setSelectedIdCliente(sessionStorage.getItem('idCliente')!);

      if (sessionStorage.getItem('idCantiere')) {
        this.authStore.setSelectedIdCantiere(
          sessionStorage.getItem('idCantiere'),
        );
      } else if (!isAdmin) {
        this.router.navigate(['/home']);
        return;
      }
    } else {
      this.router.navigate(['/home']);
    }
  }

  async logout() {
    await this.authStore.logout();
  }
  readonly mainPage = input<any>();

  openSidebar() {
    this.visibleSidebar2 = true;
  }

  closeSidebar() {
    this.visibleSidebar2 = false;
  }

  changeCantiere(cantiere: CantiereDTO) {
    this.selectedCantiere = cantiere;
    this.authStore.setSelectedCantiere(cantiere);
  }
}
